import { z } from "zod";

/* ===========================================================================
 * US-E18.F4-0197 (@UC9284–@UC9287) — ĐÓNG GÓP PHI TIỀN MẶT (in-kind: công sức / hiện vật / dịch vụ /
 * voucher / điểm / cashback). Phase Post-MVP — @gate money,ledger 🔴.
 *
 * 🔴🔴 BẤT BIẾN CỐT LÕI: ghi công KHÔNG làm tăng TIỀN MẶT của quỹ. Bút toán DEBIT EXPENSE /
 * CREDIT INCOME (0 chân CASH) ⇒ Σ CASH của sổ BẤT BIẾN. Response TRẢ LẠI `fundCashMinor` (Σ CASH SAU
 * khi ghi) để client TỰ KIỂM: bằng trước ⇒ không bịa tiền.
 *
 * TÁCH BẠCH tiền vs phi-tiền: `/contributions-summary` trả `cashMinor` và `inKindMinor` RIÊNG —
 * in-kind KHÔNG cộng vào số dư tiền (`fundCashMinor` = Σ CASH ledger, không dính in-kind).
 *
 * INVARIANTS: B1 tiền STRING minor (^\d+$, > 0) — KHÔNG float · C5 `.strict()` chặn over-post ·
 * P6.6 định giá CÓ DẤU VẾT (`valuationReason` bắt buộc — ai/vì sao) · ghi hộ member cần OPT-IN.
 * ========================================================================= */

const POSITIVE_INTEGER_STRING_PATTERN = /^\d+$/;
const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;
const PG_BIGINT_MAX = 9223372036854775807n;
const AMOUNT_MAX_DIGITS = 19;
const DESCRIPTION_MAX = 500;
const REASON_MAX = 500;
const IDEMPOTENCY_KEY_MAX = 200;

/** Loại đóng góp phi tiền mặt — whitelist = DB CHECK `in_kind_contributions_kind_check`. */
export const IN_KIND_KIND = {
  LABOR: "labor",
  GOODS: "goods",
  SERVICE: "service",
  VOUCHER: "voucher",
  POINTS: "points",
  CASHBACK: "cashback",
} as const;

export type InKindKindValue = (typeof IN_KIND_KIND)[keyof typeof IN_KIND_KIND];

export const inKindKindSchema = z.enum([
  IN_KIND_KIND.LABOR,
  IN_KIND_KIND.GOODS,
  IN_KIND_KIND.SERVICE,
  IN_KIND_KIND.VOUCHER,
  IN_KIND_KIND.POINTS,
  IN_KIND_KIND.CASHBACK,
]);

/** 422 — ghi công HỘ member khác mà không có opt-in (chống áp đặt nghĩa vụ/công trạng). */
export const IN_KIND_CONSENT_REQUIRED_CODE = "IN_KIND_CONSENT_REQUIRED" as const;
/** 422 — member không thuộc nhóm sở hữu sổ (chống poison đóng góp). */
export const IN_KIND_MEMBER_INVALID_CODE = "IN_KIND_MEMBER_INVALID" as const;

export const IN_KIND_VALUE_INVALID_MESSAGE =
  "Giá trị định giá phải là số nguyên dương (đơn vị nhỏ nhất)";

const valuedMinorString = z
  .string()
  .trim()
  .min(1, IN_KIND_VALUE_INVALID_MESSAGE)
  .max(AMOUNT_MAX_DIGITS, IN_KIND_VALUE_INVALID_MESSAGE)
  .regex(POSITIVE_INTEGER_STRING_PATTERN, IN_KIND_VALUE_INVALID_MESSAGE)
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, IN_KIND_VALUE_INVALID_MESSAGE);

/**
 * Body POST /v1/funds/:id/in-kind — ghi nhận đóng góp phi tiền mặt.
 * `memberId` vắng ⇒ nguồn NGOÀI nhóm (nhà tài trợ tặng hiện vật / NCC hoàn cashback).
 * `memberConsent` = member ĐÃ ĐỒNG Ý bị ghi công (BẮT BUỘC khi treasurer/owner ghi HỘ người khác —
 * P6.6 thoả thuận, không áp đặt). Tự ghi công của MÌNH ⇒ consent hiển nhiên.
 * `valuationReason` BẮT BUỘC: định giá phải có LÝ DO + người chịu trách nhiệm (server gắn valuedBy).
 */
export const recordInKindRequestSchema = z
  .object({
    kind: inKindKindSchema,
    valuedMinor: valuedMinorString,
    memberId: z.string().uuid().optional(),
    description: z.string().trim().max(DESCRIPTION_MAX).optional(),
    valuationReason: z.string().trim().min(1).max(REASON_MAX),
    memberConsent: z.boolean().optional(),
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
  })
  .strict();

export type RecordInKindRequest = z.infer<typeof recordInKindRequestSchema>;

/** Dấu vết định giá (P6.6) — ai định giá, khi nào, vì sao, có opt-in không. */
export const valuationBasisSchema = z
  .object({
    valuedBy: z.string().uuid(),
    valuedAt: z.string().datetime(),
    reason: z.string(),
    /** 'self' = tự ghi công mình · 'member_consented' = ghi hộ có opt-in · 'external' = nguồn ngoài. */
    consent: z.enum(["self", "member_consented", "external"]),
  })
  .strict();

export type ValuationBasis = z.infer<typeof valuationBasisSchema>;

/**
 * Kết quả ghi công. 🔴 `fundCashMinor` = Σ CASH của sổ SAU khi ghi — PHẢI BẰNG trước khi ghi
 * (client verify được: ghi công KHÔNG bịa tiền).
 */
export const recordInKindResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    memberId: z.string().uuid().nullable(),
    kind: inKindKindSchema,
    valuedMinor: z.string().regex(POSITIVE_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    description: z.string().nullable(),
    valuationBasis: valuationBasisSchema,
    ledgerTxId: z.string().uuid(),
    /** txType TÁI DÙNG: SPONSORSHIP_MEMBER | SPONSORSHIP_EXTERNAL | OTHER_INCOME. */
    txType: z.enum(["SPONSORSHIP_MEMBER", "SPONSORSHIP_EXTERNAL", "OTHER_INCOME"]),
    /** 🔴 Σ CASH của sổ SAU khi ghi công — BẤT BIẾN (không có chân CASH trong bút toán). */
    fundCashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    recordedAt: z.string().datetime(),
  })
  .strict();

export type RecordInKindResponse = z.infer<typeof recordInKindResponseSchema>;

/** Đóng góp phi tiền của 1 member, tách theo loại (báo cáo công bằng). */
export const memberContributionSchema = z
  .object({
    memberId: z.string().uuid(),
    /** Σ tiền THẬT đã đóng (ledger CONTRIBUTION — MEMBER_RECEIVABLE của member). */
    cashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Σ giá trị PHI TIỀN đã ghi nhận (KHÔNG phải tiền — không nằm trong số dư quỹ). */
    inKindMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    inKindByKind: z.record(inKindKindSchema, z.string().regex(MONEY_INTEGER_STRING_PATTERN)),
    /** cash + in-kind — CHỈ dùng đối soát CÔNG BẰNG (chia lời/quyết toán), KHÔNG phải số dư tiền. */
    totalValueMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type MemberContribution = z.infer<typeof memberContributionSchema>;

/**
 * GET /v1/funds/:id/contributions-summary — tổng hợp đóng góp TÁCH BẠCH tiền vs phi-tiền.
 * 🔴 `fundCashMinor` (Σ CASH ledger — SỐ DƯ TIỀN THẬT) KHÔNG bao gồm `totalInKindMinor`.
 */
export const contributionsSummaryResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    members: z.array(memberContributionSchema),
    totalCashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    totalInKindMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Σ CASH của sổ (số dư TIỀN) — in-kind KHÔNG cộng vào đây. */
    fundCashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type ContributionsSummaryResponse = z.infer<typeof contributionsSummaryResponseSchema>;
